import { useEffect, useState } from "react";
import { asyncWithLDProvider, useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { FEATURE_FLAG_DEFAULTS, type FeatureFlagKey, type FeatureFlags } from "@athena/types";

/**
 * The one place a browser app reads a feature flag.
 *
 * Exported from `@athena/config/flags` rather than the package root on purpose: `common/integrations`
 * imports `@athena/config` from Lambda code, and a React import in the root export would pull React
 * into every backend bundle.
 *
 * Both web apps had their own placeholder copy of this hook returning a hardcoded default, with a
 * comment promising to wire the SDK later. This is that wiring, once.
 */

/**
 * Who the flags are evaluated for. Mirrors the server-side `FlagSubject` so a flag targeted at one
 * organization resolves the same way on both sides.
 */
export interface FlagIdentity {
    userId?: string | null;
    organizationId?: string | null;
}

/**
 * Builds a **multi-context**: a `user` context, plus an `organization` context when we know which
 * organization the person belongs to.
 *
 * The organization has to be its own context kind, not an attribute on the user, because that is
 * how the flags are targeted: the rollout lists organization ids as individual targets on the
 * `organization` kind. A single `user` context contains no organization context, so those targets
 * could never match it — every evaluation fell through to the default rule and served `false`,
 * which read as "the flag is off for everyone" no matter how the org id was passed.
 */
export const flagContextFor = (identity: FlagIdentity) => {
    // Inside a multi-context each entry is keyed *by* its kind, so the nested objects carry no
    // `kind` of their own — the SDK would not reject one, it would just attach a stray attribute
    // called "kind" to the context and show it that way in LaunchDarkly.
    const user = identity.userId ? { key: identity.userId } : { key: "anonymous", anonymous: true };

    // No organization context at all when it is unknown, rather than one keyed on a placeholder:
    // an "unknown" key would be a real context that targeting rules could match on.
    if (!identity.organizationId) return { kind: "user" as const, ...user };

    return {
        kind: "multi" as const,
        user,
        organization: { key: identity.organizationId },
    };
};

/**
 * Builds the provider component.
 *
 * Async because the SDK resolves its initial flag values before rendering, which avoids the flash of
 * every flag being false on first paint. A missing client id resolves to a provider that serves
 * declared defaults, so local development without LaunchDarkly still runs.
 */
export const createFlagProvider = async (clientSideID: string | undefined, identity: FlagIdentity) => {
    if (!clientSideID) {
        // eslint-disable-next-line no-console
        console.warn("LaunchDarkly client id is not set — feature flags will serve their defaults.");
        return ({ children }: { children: React.ReactNode }) => children as React.ReactElement;
    }

    return asyncWithLDProvider({
        clientSideID,
        context: flagContextFor(identity),
        // Without this the SDK reports every flag as undefined until the first payload lands, which
        // reads as "off" for exactly as long as the network takes.
        options: { bootstrap: "localStorage" },
    });
};

/**
 * Reads one flag.
 *
 * Falls back to the flag's declared default, which is what makes this safe to call outside a provider
 * — a test that renders a component in isolation gets the default rather than a crash.
 *
 * LaunchDarkly camel-cases flag keys in the payload it hands the React SDK, so a kebab-case key like
 * `some-flag` arrives as `someFlag`. Both spellings are checked rather than relying on that transform,
 * because it is a detail of the SDK and not of our key. Our keys are camelCase already, which makes
 * the fallback a no-op today — it stays for the first kebab-case key somebody adds in LaunchDarkly.
 */
export const useFeatureFlag = (flag: FeatureFlagKey): boolean => {
    const flags = useFlags() as Record<string, boolean | undefined>;
    const camel = flag.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
    return flags[flag] ?? flags[camel] ?? FEATURE_FLAG_DEFAULTS[flag];
};

export type { FeatureFlagKey, FeatureFlags };

/**
 * Re-targets the flag evaluation once the signed-in person is known.
 *
 * The provider is created before Auth0 resolves, so the first evaluation is anonymous. Without this
 * step every caller stays anonymous forever and a flag targeted at one organization can never match —
 * which is the whole mechanism for rolling one out to a single partner first.
 *
 * Cheap to call repeatedly: it only re-identifies when the key or organization actually changes.
 *
 * **Returns whether the flags on hand are this person's**, which is not the same question as what
 * any flag says. Until `identify` resolves, every flag still holds its anonymous value — which for
 * an organization-targeted flag is the default rule, i.e. off. A caller that merely *renders* on a
 * flag can ignore this and light up a moment later; a caller that makes an irreversible decision
 * from it (a route guard that redirects) has to wait, or it decides on the wrong answer.
 */
export const useFlagIdentity = (identity: FlagIdentity): boolean => {
    const client = useLDClient();
    const key = identity.userId ?? null;
    const org = identity.organizationId ?? null;
    const [
        targeted,
        setTargeted,
    ] = useState(false);

    useEffect(() => {
        if (!client || !key) return;

        // Cleared first, so a change of person is not reported as already-targeted while the new
        // identify is still in flight — that window would serve the previous user's flags.
        setTargeted(false);
        let current = true;
        void client.identify(flagContextFor({ userId: key, organizationId: org })).then(
            () => current && setTargeted(true),
            // An identify that fails leaves the anonymous flags in place. Report targeted anyway:
            // the values are wrong, but they are the best available, and a caller blocked on this
            // would otherwise wait forever.
            () => current && setTargeted(true),
        );
        return () => {
            current = false;
        };
    }, [
        client,
        key,
        org,
    ]);

    return targeted;
};
